import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Scale, AlertTriangle, TrendingDown, ArrowRight,
  RefreshCw, ChevronDown, ChevronUp, Info, ShieldCheck,
  Package, Store, Eye, CheckCircle, XCircle, Clock,
  ArrowLeftRight, Tag, Percent, HelpCircle, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiError } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DISPOSITION_CONFIG = {
  HOLD: { label: 'Hold', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: CheckCircle, desc: 'No intervention needed' },
  MONITOR: { label: 'Monitor', color: 'bg-amber-50 text-amber-700 border-amber-300', icon: Eye, desc: 'Watch closely' },
  TRANSFER_CANDIDATE: { label: 'Transfer Candidate', color: 'bg-blue-50 text-blue-700 border-blue-300', icon: ArrowLeftRight, desc: 'Consider inter-store transfer' },
  PROMOTION_CANDIDATE: { label: 'Promotion Candidate', color: 'bg-purple-50 text-purple-700 border-purple-300', icon: Tag, desc: 'Consider promotional intervention' },
  MARKDOWN_CANDIDATE: { label: 'Markdown Candidate', color: 'bg-rose-50 text-rose-700 border-rose-300', icon: Percent, desc: 'Candidate for markdown consideration' },
  INSUFFICIENT_EVIDENCE: { label: 'Insufficient Evidence', color: 'bg-gray-100 text-gray-500 border-gray-300', icon: HelpCircle, desc: 'More data needed' },
};

const QUALITY_CONFIG = {
  HIGH: { label: 'High', color: 'text-emerald-600' },
  MODERATE: { label: 'Moderate', color: 'text-amber-600' },
  LOW: { label: 'Low', color: 'text-orange-600' },
  INSUFFICIENT: { label: 'Insufficient', color: 'text-gray-400' },
};

const DispositionBadge = ({ disposition }) => {
  const config = DISPOSITION_CONFIG[disposition] || DISPOSITION_CONFIG.INSUFFICIENT_EVIDENCE;
  const Icon = config.icon;
  return (
    <span data-testid={`disposition-badge-${disposition}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${config.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

const QualityBadge = ({ quality }) => {
  const config = QUALITY_CONFIG[quality] || QUALITY_CONFIG.INSUFFICIENT;
  return <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>;
};

const SummaryCard = ({ title, count, icon: Icon, variant }) => {
  const variants = {
    default: 'border-l-4 border-l-slate-400',
    warning: 'border-l-4 border-l-amber-400',
    info: 'border-l-4 border-l-blue-400',
    promo: 'border-l-4 border-l-purple-400',
    danger: 'border-l-4 border-l-rose-400',
    muted: 'border-l-4 border-l-gray-300',
  };
  return (
    <Card className={`${variants[variant] || variants.default}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1" data-testid={`summary-${title.toLowerCase().replace(/\s+/g,'-')}`}>{count}</p>
          </div>
          <Icon className="w-8 h-8 text-muted-foreground/40" />
        </div>
      </CardContent>
    </Card>
  );
};

const CandidateDetail = ({ candidate, onCreateRecommendation }) => {
  const [expanded, setExpanded] = useState(false);
  const entity = candidate.entity || {};
  const isEligible = ['TRANSFER_CANDIDATE', 'PROMOTION_CANDIDATE', 'MARKDOWN_CANDIDATE'].includes(candidate.disposition);
  const config = DISPOSITION_CONFIG[candidate.disposition] || DISPOSITION_CONFIG.INSUFFICIENT_EVIDENCE;

  return (
    <div data-testid={`candidate-row-${entity.product_code}`} className="border rounded-lg bg-white">
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{entity.product_code || 'Unknown'}</p>
            <p className="text-xs text-muted-foreground">Store: {entity.store_code || 'N/A'}</p>
          </div>
          <DispositionBadge disposition={candidate.disposition} />
          <div className="hidden sm:block">
            <p className="text-xs text-muted-foreground">Evidence Quality</p>
            <QualityBadge quality={candidate.evidence_quality} />
          </div>
          <div className="hidden md:block text-xs text-muted-foreground max-w-xs truncate">
            {config.desc}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEligible && (
            <Button
              data-testid={`create-rec-btn-${entity.product_code}`}
              variant="outline" size="sm"
              onClick={(e) => { e.stopPropagation(); onCreateRecommendation(candidate); }}
            >
              Create Recommendation
            </Button>
          )}
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t p-4 space-y-4 bg-muted/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">Reason Codes</h4>
              <div className="flex flex-wrap gap-1">
                {(candidate.reason_codes || []).map((rc, i) => (
                  <span key={i} className="px-2 py-0.5 bg-muted rounded text-xs">{rc}</span>
                ))}
                {(!candidate.reason_codes || candidate.reason_codes.length === 0) && (
                  <span className="text-xs text-muted-foreground">None</span>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">Limitations</h4>
              <div className="flex flex-wrap gap-1">
                {(candidate.limitations || []).map((l, i) => (
                  <span key={i} className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs">{l}</span>
                ))}
                {(!candidate.limitations || candidate.limitations.length === 0) && (
                  <span className="text-xs text-muted-foreground">None</span>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">Conflicting Signals</h4>
              {(candidate.conflicting_signals || []).length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {candidate.conflicting_signals.map((cs, i) => (
                    <span key={i} className="px-2 py-0.5 bg-yellow-50 text-yellow-700 rounded text-xs">{cs}</span>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">None</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {candidate.inventory_evidence && (
              <EvidenceCard title="Inventory" data={candidate.inventory_evidence} />
            )}
            {candidate.demand_evidence && (
              <EvidenceCard title="Demand" data={candidate.demand_evidence} />
            )}
            {candidate.network_evidence && (
              <EvidenceCard title="Network" data={candidate.network_evidence} />
            )}
            {candidate.commercial_evidence && (
              <EvidenceCard title="Commercial" data={candidate.commercial_evidence} />
            )}
          </div>

          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>Pressure: <strong>{candidate.pressure_level}</strong></span>
            <span>Policy: {candidate.policy_version}</span>
            {candidate.analysis_as_of && <span>As of: {new Date(candidate.analysis_as_of).toLocaleDateString()}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const EvidenceCard = ({ title, data }) => {
  if (!data) return null;
  const entries = Object.entries(data).filter(([k, v]) => v !== null && v !== undefined && k !== '_type');
  if (entries.length === 0) return null;
  return (
    <div className="bg-white border rounded p-3">
      <h5 className="text-xs font-semibold mb-2">{title}</h5>
      <div className="space-y-1">
        {entries.slice(0, 5).map(([k, v]) => (
          <div key={k} className="flex justify-between text-xs">
            <span className="text-muted-foreground">{k.replace(/_/g, ' ')}</span>
            <span className="font-medium">{typeof v === 'number' ? v.toFixed(1) : String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CommercialInterventionDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [dispositionFilter, setDispositionFilter] = useState('');
  const [qualityFilter, setQualityFilter] = useState('');
  const pageSize = 20;

  const getToken = () => localStorage.getItem('token');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (dispositionFilter) params.set('disposition_filter', dispositionFilter);
      if (qualityFilter) params.set('evidence_quality_filter', qualityFilter);

      const response = await axios.get(
        `${BACKEND_URL}/api/commercial-intervention/dashboard?${params}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setData(response.data);
    } catch (err) {
      toast.error('Failed to load commercial intervention data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, dispositionFilter, qualityFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateRecommendation = async (candidate) => {
    try {
      const entity = candidate.entity || {};
      const response = await axios.post(
        `${BACKEND_URL}/api/commercial-intervention/create-recommendation`,
        {
          product_code: entity.product_code,
          store_code: String(entity.store_code),
          disposition: candidate.disposition,
          reason_codes: candidate.reason_codes || [],
          evidence_quality: candidate.evidence_quality,
          limitations: candidate.limitations || [],
          policy_version: candidate.policy_version,
          authority_provenance: candidate.authority_provenance,
          analysis_as_of: candidate.analysis_as_of,
        },
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } }
      );
      if (response.data.created) {
        toast.success(`Recommendation created for ${entity.product_code}`);
      } else {
        toast.info(response.data.reason || 'Recommendation already exists');
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to create recommendation'));
    }
  };

  const summary = data?.summary || {};
  const candidates = data?.candidates || [];
  const pagination = data?.pagination || {};

  if (loading && !data) {
    return (
      <div data-testid="commercial-intervention-loading" className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Evaluating commercial interventions...</span>
      </div>
    );
  }

  if (data && !data.hasData) {
    return (
      <div data-testid="commercial-intervention-empty" className="flex flex-col items-center justify-center h-64 text-center">
        <Scale className="w-12 h-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium">No Inventory Data Available</h3>
        <p className="text-sm text-muted-foreground mt-1">{data.message}</p>
      </div>
    );
  }

  return (
    <div data-testid="commercial-intervention-dashboard" className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Commercial Intervention</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Deterministic candidate dispositions from CommercialInterventionEngine
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.policy_version && (
            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              Policy: {data.policy_version}
            </span>
          )}
          <Button data-testid="refresh-btn" variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard title="Hold" count={summary.hold || 0} icon={CheckCircle} variant="default" />
        <SummaryCard title="Monitor" count={summary.monitor || 0} icon={Eye} variant="warning" />
        <SummaryCard title="Transfer" count={summary.transfer_candidate || 0} icon={ArrowLeftRight} variant="info" />
        <SummaryCard title="Promotion" count={summary.promotion_candidate || 0} icon={Tag} variant="promo" />
        <SummaryCard title="Markdown" count={summary.markdown_candidate || 0} icon={Percent} variant="danger" />
        <SummaryCard title="Insufficient" count={summary.insufficient_evidence || 0} icon={HelpCircle} variant="muted" />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Candidates ({pagination.total || 0} products)</CardTitle>
            <div className="flex items-center gap-2">
              <select
                data-testid="disposition-filter"
                value={dispositionFilter}
                onChange={(e) => { setDispositionFilter(e.target.value); setPage(1); }}
                className="text-xs border rounded px-2 py-1.5 bg-white"
              >
                <option value="">All Dispositions</option>
                <option value="HOLD">Hold</option>
                <option value="MONITOR">Monitor</option>
                <option value="TRANSFER_CANDIDATE">Transfer Candidate</option>
                <option value="PROMOTION_CANDIDATE">Promotion Candidate</option>
                <option value="MARKDOWN_CANDIDATE">Markdown Candidate</option>
                <option value="INSUFFICIENT_EVIDENCE">Insufficient Evidence</option>
              </select>
              <select
                data-testid="quality-filter"
                value={qualityFilter}
                onChange={(e) => { setQualityFilter(e.target.value); setPage(1); }}
                className="text-xs border rounded px-2 py-1.5 bg-white"
              >
                <option value="">All Quality</option>
                <option value="HIGH">High</option>
                <option value="MODERATE">Moderate</option>
                <option value="LOW">Low</option>
                <option value="INSUFFICIENT">Insufficient</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No candidates match the selected filters.</p>
          ) : (
            candidates.map((c, i) => (
              <CandidateDetail key={i} candidate={c} onCreateRecommendation={handleCreateRecommendation} />
            ))
          )}

          {pagination.total_pages > 1 && (
            <div data-testid="pagination-controls" className="flex items-center justify-between pt-4">
              <span className="text-xs text-muted-foreground">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pagination.total_pages} onClick={() => setPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground flex items-center gap-4">
        <span className="flex items-center gap-1"><Info className="w-3 h-3" /> Dispositions are deterministic — computed by CommercialInterventionEngine only</span>
        {data?.latency_ms && <span>Evaluated in {data.latency_ms}ms</span>}
      </div>
    </div>
  );
};

export default CommercialInterventionDashboard;
